/**
 * @fileoverview LinkToExistingSection component for linking metadata to existing AIIO items.
 * Provides a searchable combobox interface to select and link existing items, with the ability to unlink.
 */

import { useTranslation } from "@/i18n";
import { Link, Unlink } from "lucide-react";
import type { DropdownItem } from "../types";
import SearchableCombobox from "./SearchableCombobox";

/**
 * Props for the LinkToExistingSection component.
 */
interface LinkToExistingSectionProps {
  /** Whether linking functionality should be shown (hidden for new items) */
  readonly isVisible: boolean;
  /** German label for the category (e.g., "Abteilung", "Rolle") */
  readonly categoryLabel: string;
  /** Available items that can be linked to */
  readonly availableItems: readonly DropdownItem[];
  /** Field name to use for displaying item names */
  readonly nameField: string;
  /** Currently linked item ID, if any */
  readonly linkedId: number | undefined;
  /** Current name value (used for display when linked) */
  readonly currentName: string;
  /** Whether the dropdown is currently visible */
  readonly isDropdownOpen: boolean;
  /** Callback to toggle dropdown visibility */
  readonly onToggleDropdown: () => void;
  /** Callback when an item is selected for linking */
  readonly onLinkItem: (item: DropdownItem) => void;
  /** Callback to unlink the current item */
  readonly onUnlink: () => void;
}

/**
 * LinkToExistingSection allows users to link metadata to existing AIIO items.
 * Displays a searchable combobox of available items and provides unlink functionality.
 *
 * @param props - Component props
 * @returns Link to existing section UI, or null if not visible
 */
export default function LinkToExistingSection({
  isVisible,
  categoryLabel,
  availableItems,
  nameField,
  linkedId,
  currentName,
  isDropdownOpen,
  onToggleDropdown,
  onLinkItem,
  onUnlink,
}: LinkToExistingSectionProps) {
  const { t } = useTranslation();

  if (!isVisible) {
    return null;
  }

  const getItemName = (item: DropdownItem): string => {
    return (item[nameField as keyof DropdownItem] as string) || "Unknown";
  };

  return (
    <div className="space-y-2">
      <label className="text-sm text-[#18181b] leading-5">
        {t("documentReview.modal.linkToExisting", { category: categoryLabel })}
      </label>
      <div className="relative dropdown-container">
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <SearchableCombobox
              items={availableItems}
              isOpen={isDropdownOpen}
              getItemName={getItemName}
              getItemKey={(item) => item.id}
              renderTrigger={() =>
                linkedId ? (
                  <>
                    <Link className="w-4 h-4 text-[#71717a]" />
                    <span>{currentName}</span>
                  </>
                ) : (
                  <span>{t("documentReview.modal.selectExisting")}</span>
                )
              }
              onSelect={onLinkItem}
              onToggle={onToggleDropdown}
              searchPlaceholder={t("common.searching")}
              noResultsMessage={t("documentReview.modal.noResults")}
              isPlaceholder={!linkedId}
            />
          </div>

          {linkedId && (
            <button
              type="button"
              onClick={onUnlink}
              className="h-10 px-3 bg-white border border-[#e4e4e7] text-[#18181b] text-sm rounded-md hover:bg-[#f4f4f5] transition-colors flex items-center gap-2"
              title={t("common.buttons.unlink")}
            >
              <Unlink className="w-4 h-4" />
              {t("common.buttons.unlink")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
